import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ENTITY_COLORS = {
    card: "text-blue-700 bg-blue-50 border-blue-200",
    expense: "text-emerald-700 bg-emerald-50 border-emerald-200",
    statement: "text-amber-700 bg-amber-50 border-amber-200",
    payment: "text-stone-900 bg-stone-100 border-stone-200",
    user: "text-red-700 bg-red-50 border-red-200",
    bank_account: "text-stone-700 bg-stone-50 border-stone-200",
    subscription: "text-emerald-700 bg-emerald-50 border-emerald-200",
    dispute: "text-red-700 bg-red-50 border-red-200",
    document: "text-blue-700 bg-blue-50 border-blue-200",
};

// Sidebar deep-links pass ?event=login/export/document_download/stmt_pw_reveal
// — translate those tags into the underlying entity + action filter combo.
const EVENT_PRESETS = {
    login:              { entity: "user",      action: "all" },
    export:             { entity: "all",       action: "exported" },
    document_download:  { entity: "document",  action: "all" },
    stmt_pw_reveal:     { entity: "statement", action: "stmt_pw_reveal" },
};

export default function AuditTrail() {
    const [sp] = useSearchParams();
    const eventTag = sp.get("event");
    const preset = eventTag ? EVENT_PRESETS[eventTag] : null;
    const [rows, setRows] = useState([]);
    const [entity, setEntity] = useState(preset?.entity || "all");
    const [action, setAction] = useState(preset?.action || "all");

    useEffect(() => {
        // Re-sync when the URL preset changes (e.g. deep-linking between
        // sidebar submenu items without re-mounting the page).
        if (preset) {
            setEntity(preset.entity);
            setAction(preset.action);
        }
    }, [eventTag, preset]);

    useEffect(() => {
        const params = {};
        if (entity !== "all") params.entity = entity;
        if (action !== "all") params.action = action;
        api.get("/audit-logs", { params }).then((r) => setRows(r.data));
    }, [entity, action]);

    return (
        <div data-testid="audit-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Audit Trail</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Who did what, when.</h1>
                    <p className="text-sm text-stone-500 mt-3">Complete change log for every action across the system.</p>
                </div>
                <div className="flex gap-2">
                    <Select value={entity} onValueChange={setEntity}>
                        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All entities</SelectItem>
                            <SelectItem value="card">Cards</SelectItem>
                            <SelectItem value="expense">Expenses</SelectItem>
                            <SelectItem value="statement">Statements</SelectItem>
                            <SelectItem value="payment">Payments</SelectItem>
                            <SelectItem value="user">Users</SelectItem>
                            <SelectItem value="bank_account">Bank Accounts</SelectItem>
                            <SelectItem value="subscription">Subscriptions</SelectItem>
                            <SelectItem value="dispute">Disputes</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={action} onValueChange={setAction}>
                        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All actions</SelectItem>
                            <SelectItem value="created">Created</SelectItem>
                            <SelectItem value="updated">Updated</SelectItem>
                            <SelectItem value="deleted">Deleted / Archived</SelectItem>
                            <SelectItem value="uploaded">Uploaded</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Time</th><th>User</th><th>Role</th><th>Action</th><th>Entity</th><th>From</th><th>Details</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td className="tabular-nums text-xs whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                                    <td>{r.user_email || "—"}</td>
                                    <td className="text-xs uppercase tracking-widest text-stone-500">{r.user_role || "—"}</td>
                                    <td>{r.action}</td>
                                    <td>
                                        <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${ENTITY_COLORS[r.entity] || "bg-stone-50 border-stone-200 text-stone-700"}`}>
                                            {r.entity}
                                        </span>
                                    </td>
                                    <td className="text-[11px] text-stone-600 whitespace-nowrap" data-testid={`audit-geo-${r.id}`}>
                                        {r.geo_city || r.geo_country ? (
                                            <span className="inline-flex items-center gap-1">
                                                {r.geo_country_code && (
                                                    <span className="font-mono-figure text-stone-500">
                                                        {r.geo_country_code}
                                                    </span>
                                                )}
                                                <span>{[r.geo_city, r.geo_country].filter(Boolean).join(", ")}</span>
                                            </span>
                                        ) : (
                                            <span className="text-stone-400">—</span>
                                        )}
                                        {r.ip && (
                                            <div className="font-mono-figure text-[10px] text-stone-400 mt-0.5">{r.ip}</div>
                                        )}
                                    </td>
                                    <td className="text-xs text-stone-500">{r.details && Object.keys(r.details).length > 0 ? JSON.stringify(r.details) : "—"}</td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-stone-500 py-14">No audit entries.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
